// src/views/reports/StaleAccountCreditsReportView.js
// stalecred-3b8e1d60 -- SEAM 13: STALE ACCOUNT CREDITS report + manual write-off.
// Money we are HOLDING FOR customers (opposite direction from Collections). Lists every
// open account credit that has sat unapplied for 12+ months and lets an operator write it
// off with a required reason. No automatic expiry, ever -- write-off is a deliberate act.

import React, { useState, useEffect, useCallback } from 'react';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { Loader2, ArrowLeft, Download, X, AlertTriangle, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { exportToCSV } from '../../utils/csvExport';
import { can, PERMISSIONS } from '../../utils/permissions';

// --- WRITE-OFF CONFIRM MODAL ---
// A required reason + a plain-English, no-undo warning before closing real money.
const WriteOffModal = ({ credit, onClose, onConfirm, submitting }) => {
    const [reason, setReason] = useState('');
    if (!credit) return null;

    const canSubmit = reason.trim().length > 0 && !submitting;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-5 border-b bg-gray-50">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <AlertTriangle size={20} className="text-amber-600" /> Write off account credit
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Customer</span>
                            <span className="font-semibold text-gray-900">{credit.customerName}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                            <span className="text-gray-500">Amount to write off</span>
                            <span className="font-bold text-gray-900">{formatCurrency(credit.amount)}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                            <span className="text-gray-500">Originated</span>
                            <span className="text-gray-700">{credit.originatedAt || 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                            <span className="text-gray-500">Last activity</span>
                            <span className="text-gray-700">{credit.lastActivityAt || 'None since origination'}</span>
                        </div>
                    </div>

                    {/* stalecred-3b8e1d60 -- plainer wording (Seam 13 review): the customer is
                        still owed this money; we're only stopping tracking it as available. */}
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        <p className="font-semibold mb-1">This can't be undone.</p>
                        <p>
                            {credit.customerName} is still owed this {formatCurrency(credit.amount)} and can still
                            ask for it — writing it off only stops Spatchy from tracking it as available credit.
                            It does not refund anyone and does not change anything in QuickBooks.
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder="Why is this credit being written off?"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                        />
                    </div>
                </div>

                <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onConfirm(credit, reason.trim())}
                        disabled={!canSubmit}
                        className="px-5 py-2 bg-amber-600 text-white rounded font-medium hover:bg-amber-700 shadow-sm disabled:opacity-50 flex items-center gap-2"
                    >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                        Write off {formatCurrency(credit.amount)}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN VIEW ---
const StaleAccountCreditsReportView = ({ currentUserProfile }) => {
    const [credits, setCredits] = useState([]);
    const [summary, setSummary] = useState({ count: 0, totalHeld: 0, staleMonths: 12 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null); // credit being written off
    const [submitting, setSubmitting] = useState(false);

    const mayWriteOff = can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const fn = httpsCallable(functions, 'getStaleAccountCredits');
            const res = await fn({});
            const data = res.data || {};
            setCredits(data.credits || []);
            setSummary({ count: data.count || 0, totalHeld: data.totalHeld || 0, staleMonths: data.staleMonths || 12 });
        } catch (e) {
            console.error('Error loading stale credits:', e);
            setError(e.message || 'Failed to load stale credits.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleWriteOff = async (credit, reason) => {
        setSubmitting(true);
        try {
            const fn = httpsCallable(functions, 'writeOffAccountCredit');
            await fn({ creditId: credit.creditId, reason });
            // Drop it from the list and update the running total locally.
            setCredits((prev) => prev.filter((c) => c.creditId !== credit.creditId));
            setSummary((prev) => ({
                ...prev,
                count: Math.max(0, prev.count - 1),
                totalHeld: Math.round((prev.totalHeld - credit.amount) * 100) / 100,
            }));
            setSelected(null);
        } catch (e) {
            console.error('Write-off failed:', e);
            alert('Write-off failed: ' + (e.message || 'Unknown error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleExport = () => {
        if (credits.length === 0) return alert('No data to export.');
        const rows = credits.map((c) => ({
            Customer: c.customerName,
            Amount_Held: c.amount.toFixed(2),
            Originated: c.originatedAt || 'N/A',
            Last_Activity: c.lastActivityAt || 'N/A',
            Dormant_Months: c.dormantMonths != null ? c.dormantMonths : 'N/A',
            Type: c.type,
            Origin: c.origin,
        }));
        exportToCSV(rows, `Stale_Account_Credits_${new Date().toISOString().split('T')[0]}.csv`);
    };

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-purple-600" /></div>;

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100 transition-colors">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Stale Account Credits</h1>
                        <p className="text-gray-500 text-sm">
                            Money you're holding for customers, unapplied for {summary.staleMonths} months or more.
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleExport}
                    className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <Download size={18} /> Export List
                </button>
            </div>

            {/* DIRECTION CALLOUT + TOTAL */}
            <div className="mb-6 rounded-lg border border-purple-200 bg-purple-50 p-4 flex items-start gap-3">
                <Wallet size={20} className="text-purple-600 mt-0.5 shrink-0" />
                <div className="text-sm text-purple-900">
                    These are funds owed <span className="font-semibold">to</span> customers — not amounts they owe you.
                    You're holding <span className="font-bold">{formatCurrency(summary.totalHeld)}</span> across{' '}
                    <span className="font-bold">{summary.count}</span> stale {summary.count === 1 ? 'credit' : 'credits'}.
                </div>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-4">Customer</th>
                                <th className="px-6 py-4 text-right">Amount held</th>
                                <th className="px-6 py-4 text-center">Originated</th>
                                <th className="px-6 py-4 text-center">Last activity</th>
                                <th className="px-6 py-4 text-center">Dormant</th>
                                {mayWriteOff && <th className="px-6 py-4 text-center">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {credits.length === 0 ? (
                                <tr>
                                    <td colSpan={mayWriteOff ? 6 : 5} className="p-8 text-center text-gray-500">
                                        No stale credits — nothing has sat unapplied for {summary.staleMonths} months or more.
                                    </td>
                                </tr>
                            ) : (
                                credits.map((c) => (
                                    <tr key={c.creditId} className="bg-white border-b hover:bg-purple-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <p className="font-bold text-gray-900">{c.customerName}</p>
                                            <p className="text-xs text-gray-500 capitalize">{c.origin} · {c.type}</p>
                                        </td>
                                        <td className="px-6 py-4 text-right font-semibold text-gray-900">
                                            {formatCurrency(c.amount)}
                                        </td>
                                        <td className="px-6 py-4 text-center text-gray-600">{c.originatedAt || 'N/A'}</td>
                                        <td className="px-6 py-4 text-center text-gray-600">{c.lastActivityAt || '—'}</td>
                                        <td className="px-6 py-4 text-center text-gray-600">
                                            {c.dormantMonths != null ? `${c.dormantMonths} mo` : 'N/A'}
                                        </td>
                                        {mayWriteOff && (
                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => setSelected(c)}
                                                    className="px-3 py-1.5 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md border border-amber-200 text-xs font-medium"
                                                    title="Write off this credit"
                                                >
                                                    Write off
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && (
                <WriteOffModal
                    credit={selected}
                    onClose={() => setSelected(null)}
                    onConfirm={handleWriteOff}
                    submitting={submitting}
                />
            )}
        </div>
    );
};

export default StaleAccountCreditsReportView;
